'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AuthGateModal from '@/components/AuthGateModal';
import { useAuth } from '@/contexts/AuthContext';

const features = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
    label: 'Organize Anything',
    desc: 'Group items by series, era, category, or any system that makes sense to you.',
    color: 'panel-blue',
    iconBg: 'bg-[#4ECDC4]/10',
    iconText: 'text-[#4ECDC4]',
    iconHoverBg: 'group-hover:bg-[#4ECDC4]/20',
    borderHover: 'hover:border-[#4ECDC4]/30 hover:bg-[#4ECDC4]/[0.02]',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
    label: 'Track What You Own',
    desc: "Check off items as you get them. See exactly what\'s missing from any set or run.",
    color: 'panel-green',
    iconBg: 'bg-[#2ECC71]/10',
    iconText: 'text-[#2ECC71]',
    iconHoverBg: 'group-hover:bg-[#2ECC71]/20',
    borderHover: 'hover:border-[#2ECC71]/30 hover:bg-[#2ECC71]/[0.02]',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    label: 'Scan to Add',
    desc: 'Scan a barcode or type a title to add items instantly — no manual data entry.',
    color: 'panel-yellow',
    iconBg: 'bg-[#FFE566]/20',
    iconText: 'text-[#B8A000]',
    iconHoverBg: 'group-hover:bg-[#FFE566]/30',
    borderHover: 'hover:border-[#FFE566]/40 hover:bg-[#FFE566]/[0.03]',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    label: 'Grade & Value',
    desc: 'Log condition grades and track estimated value across your entire collection.',
    color: 'panel-orange',
    iconBg: 'bg-[#FF6B35]/10',
    iconText: 'text-[#FF6B35]',
    iconHoverBg: 'group-hover:bg-[#FF6B35]/20',
    borderHover: 'hover:border-[#FF6B35]/30 hover:bg-[#FF6B35]/[0.02]',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    ),
    label: 'Share Collections',
    desc: 'Generate a public link to show off your collection — no account needed to view.',
    color: 'panel-purple',
    iconBg: 'bg-[#9B59B6]/10',
    iconText: 'text-[#9B59B6]',
    iconHoverBg: 'group-hover:bg-[#9B59B6]/20',
    borderHover: 'hover:border-[#9B59B6]/30 hover:bg-[#9B59B6]/[0.02]',
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    label: 'Trade with Collectors',
    desc: 'List items for trade and connect with other collectors who want what you have.',
    color: 'acid',
    iconBg: 'bg-[#FF3D6B]/10',
    iconText: 'text-[#FF3D6B]',
    iconHoverBg: 'group-hover:bg-[#FF3D6B]/20',
    borderHover: 'hover:border-[#FF3D6B]/30 hover:bg-[#FF3D6B]/[0.02]',
  },
];

const steps = [
  {
    num: '01',
    heading: 'Create a collection',
    body: 'Name it anything — "Vintage Vinyl", "First Editions", "Trading Cards". Add as many collections as you need.',
    numColor: 'text-[#4ECDC4]',
    dotColor: 'bg-[#4ECDC4]',
  },
  {
    num: '02',
    heading: 'Add your items',
    body: 'Scan barcodes, search by title, or import a list. Each item gets its own record with condition, notes, and value.',
    numColor: 'text-[#FF6B35]',
    dotColor: 'bg-[#FF6B35]',
  },
  {
    num: '03',
    heading: 'Track the gaps',
    body: "Turn on the master list for any series to see every entry. Check off what you own, spot what's missing.",
    numColor: 'text-[#9B59B6]',
    dotColor: 'bg-[#9B59B6]',
  },
];

export default function HomePage() {
  const [showAuthGate, setShowAuthGate] = useState(false);
  const { user, loading } = useAuth();
  const router = useRouter();

  const handleOpenApp = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!loading && user) {
      router.push('/app');
    } else {
      setShowAuthGate(true);
    }
  };

  return (
    <div className="min-h-screen bg-inkwell">
      <Header />
      {/* ── HERO ── */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <hr className="w-8 border-none h-0.5" style={{ background: 'linear-gradient(to right, #4ECDC4, rgba(78,205,196,0.3), transparent)' }} />
            <span className="font-mono-data text-[#4ECDC4] text-xs tracking-widest uppercase">Collection Manager</span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-newsprint font-black leading-none mb-6">
            Keep your collection.<br />
            <em className="italic font-light text-newsprint/50">Know what you have.</em>
          </h1>

          <p className="text-newsprint/60 text-lg md:text-xl leading-relaxed max-w-2xl mb-10">
            Qollections is a simple tool for collectors of anything. Log your items, track complete sets, spot the gaps, and share what you've built.
          </p>

          {/* Colour palette preview dots */}
          <div className="flex items-center gap-2 mb-10">
            {[
              { bg: 'bg-[#4ECDC4]', label: 'teal' },
              { bg: 'bg-[#2ECC71]', label: 'green' },
              { bg: 'bg-[#FFE566]', label: 'yellow' },
              { bg: 'bg-[#FF6B35]', label: 'orange' },
              { bg: 'bg-[#9B59B6]', label: 'purple' },
              { bg: 'bg-[#FF3D6B]', label: 'red' },
            ]?.map((dot) => (
              <span key={dot?.label} className={`w-3 h-3 rounded-full ${dot?.bg} opacity-70`} />
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={handleOpenApp} className="acid-btn px-8 py-4 rounded-lg text-sm font-mono-data tracking-wider inline-flex items-center justify-center gap-2">
              START YOUR COLLECTION
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
            <Link
              href="/auth/login"
              className="px-8 py-4 rounded-lg border-2 border-newsprint/20 text-sm font-mono-data text-newsprint/60 hover:border-[#4ECDC4]/50 hover:text-[#4ECDC4] transition-colors inline-flex items-center justify-center"
            >
              SIGN IN
            </Link>
          </div>
        </div>
      </section>
      {/* ── WHAT IT DOES ── */}
      <section className="py-16 px-6 border-t-2 border-newsprint/8">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-black text-newsprint mb-2">Everything you need. Nothing you don't.</h2>
          <p className="text-newsprint/50 text-base mb-12">Built for collectors who want to manage their items, not learn new software.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features?.map((f) => (
              <div
                key={f?.label}
                className={`p-5 rounded-xl border-2 border-newsprint/8 bg-newsprint/[0.02] transition-all group ${f?.borderHover}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-4 transition-colors ${f?.iconBg} ${f?.iconText} ${f?.iconHoverBg}`}>
                  {f?.icon}
                </div>
                <div className="font-display font-black text-newsprint text-base mb-1">{f?.label}</div>
                <div className="text-newsprint/50 text-sm leading-relaxed">{f?.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── HOW IT WORKS ── */}
      <section className="py-16 px-6 border-t-2 border-newsprint/8 bg-newsprint/[0.02]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-black text-newsprint mb-2">How it works</h2>
          <p className="text-newsprint/50 text-base mb-12">Three steps to a fully tracked collection.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps?.map((s) => (
              <div key={s?.num} className="flex flex-col gap-3">
                <div className={`font-mono-data text-3xl font-bold leading-none ${s?.numColor}`}>{s?.num}</div>
                <div className="font-display font-black text-newsprint text-lg">{s?.heading}</div>
                <div className="text-newsprint/50 text-sm leading-relaxed">{s?.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ── CTA ── */}
      <section className="py-20 px-6 border-t-2 border-newsprint/8">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-newsprint mb-2">Ready to start?</h2>
            <p className="text-newsprint/50 text-base">Free to use. No credit card. Just your collection.</p>
            {/* Coloured accent bar */}
            <div className="flex gap-1 mt-4">
              <span className="h-1 w-8 rounded-full bg-[#4ECDC4]" />
              <span className="h-1 w-8 rounded-full bg-[#2ECC71]" />
              <span className="h-1 w-8 rounded-full bg-[#FFE566]" />
              <span className="h-1 w-8 rounded-full bg-[#FF6B35]" />
              <span className="h-1 w-8 rounded-full bg-[#9B59B6]" />
              <span className="h-1 w-8 rounded-full bg-[#FF3D6B]" />
            </div>
          </div>
          <button onClick={handleOpenApp} className="acid-btn px-8 py-4 rounded-lg text-sm font-mono-data tracking-wider inline-flex items-center gap-2 flex-shrink-0">
            OPEN THE APP
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </section>
      <Footer />

      {showAuthGate && <AuthGateModal onClose={() => setShowAuthGate(false)} />}
    </div>
  );
}